import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Phone, Instagram } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    club: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          language: language
        }),
      });

      if (!response.ok) {
        throw new Error('Error al enviar el mensaje');
      }

      toast({
        title: t.contact.form.success,
        description: language === 'es' 
          ? 'Gracias por tu interés. Te contactaremos pronto.'
          : language === 'en'
          ? 'Thank you for your interest. We will contact you soon.'
          : 'Obrigado pelo seu interesse. Entraremos em contato em breve.',
      });
      
      setFormData({ name: '', email: '', club: '', message: '' });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: t.contact.form.error,
        description: language === 'es'
          ? 'Por favor, intenta nuevamente o contacta directamente por email.'
          : language === 'en'
          ? 'Please try again or contact directly via email.'
          : 'Por favor, tente novamente ou entre em contato diretamente por e-mail.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-zinc-950 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2
            className="text-5xl md:text-6xl font-bold mb-4 tracking-wide"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            {t.contact.title}
          </h2>
          <div className="h-1 w-20 bg-red-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div>
            {/* Agent Info */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 mb-8">
              <h3
                className="text-2xl font-bold mb-6 text-red-600"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {t.contact.agentTitle}
              </h3>
              <div className="space-y-4">
                <p className="text-gray-400" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t.contact.agentName}
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  {t.contact.name}
                </p>
                <div className="flex items-center gap-3 text-gray-300">
                  <Phone size={20} className="text-red-600" />
                  <a href="tel:+34665497561" className="hover:text-red-600 transition-colors">
                    +34 665 497 561
                  </a>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <Mail size={20} className="text-red-600" />
                  <a href="mailto:merinovidangossy@gmail.com" className="hover:text-red-600 transition-colors">
                    merinovidangossy@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <h3
                className="text-2xl font-bold mb-6"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {t.footer.social}
              </h3>
              <div className="space-y-4">
                {/* Player Instagram */}
                <a
                  href="https://instagram.com/rockito31._"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-300 hover:text-red-600 transition-colors group"
                >
                  <div className="bg-red-600/10 p-3 rounded-lg group-hover:bg-red-600/20 transition-colors">
                    <Instagram size={24} className="text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t.contact.social.player}
                    </p>
                    <p className="font-semibold" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      @rockito31._
                    </p>
                  </div>
                </a>

                {/* Agent Instagram */}
                <a
                  href="https://instagram.com/edgarmerino19"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-300 hover:text-red-600 transition-colors group"
                >
                  <div className="bg-red-600/10 p-3 rounded-lg group-hover:bg-red-600/20 transition-colors">
                    <Instagram size={24} className="text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t.contact.social.agent}
                    </p>
                    <p className="font-semibold" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      @edgarmerino19
                    </p>
                  </div>
                </a>

                {/* Agency Instagram */}
                <a
                  href="https://instagram.com/ikonsagency"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-300 hover:text-red-600 transition-colors group"
                >
                  <div className="bg-red-600/10 p-3 rounded-lg group-hover:bg-red-600/20 transition-colors">
                    <Instagram size={24} className="text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t.contact.social.agency}
                    </p>
                    <p className="font-semibold" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      @ikonsagency
                    </p>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-gray-300 mb-2"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {t.contact.form.name}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-600 transition-colors"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-gray-300 mb-2"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {t.contact.form.email}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-600 transition-colors"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                />
              </div>

              <div>
                <label
                  htmlFor="club"
                  className="block text-gray-300 mb-2"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {t.contact.form.club}
                </label>
                <input
                  type="text"
                  id="club"
                  name="club"
                  value={formData.club}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-600 transition-colors"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-gray-300 mb-2"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {t.contact.form.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-600 transition-colors resize-none"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-red-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {isSubmitting ? 'Enviando...' : t.contact.form.send}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
